// Client-side interactivity for the resources section.
// Loaded on every /resources/* page via <script src="/resources.js" defer>.

import { getOrCreateAnonId } from './visitor';

const OS_PREF_KEY = 'vimsensei:resources:os-pref';
const READ_SENT_KEY_PREFIX = 'vimsensei:resources:read-sent:';

interface RatingAggregate {
  rating: number;
  ratingCount: number;
  ratingSum: number;
  distribution: Record<string, number>;
  userRating: number | null;
}

function setupOSTabs() {
  const containers = document.querySelectorAll<HTMLElement>('[data-os-tabs]');
  if (containers.length === 0) return;

  const stored = (() => {
    try {
      return localStorage.getItem(OS_PREF_KEY);
    } catch {
      return null;
    }
  })();

  const persist = (os: string) => {
    try {
      localStorage.setItem(OS_PREF_KEY, os);
    } catch {
      // Storage unavailable — no-op.
    }
  };

  const activate = (container: HTMLElement, os: string) => {
    const tabs = container.querySelectorAll<HTMLElement>('[data-os-tab]');
    const panes = container.querySelectorAll<HTMLElement>('[data-os-pane]');
    let matched = false;

    tabs.forEach((tab) => {
      const isMatch = tab.getAttribute('data-os-tab') === os;
      if (isMatch) matched = true;
      tab.setAttribute('aria-selected', isMatch ? 'true' : 'false');
    });

    if (!matched) return false;

    panes.forEach((pane) => {
      const isMatch = pane.getAttribute('data-os-pane') === os;
      if (isMatch) {
        pane.removeAttribute('hidden');
      } else {
        pane.setAttribute('hidden', '');
      }
    });

    const label = container.querySelector<HTMLElement>('[data-os-current]');
    if (label) label.textContent = os;
    return true;
  };

  containers.forEach((container) => {
    if (stored) activate(container, stored);

    container.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      const tab = target?.closest<HTMLElement>('[data-os-tab]');
      if (!tab || !container.contains(tab)) return;
      const os = tab.getAttribute('data-os-tab');
      if (!os) return;
      if (activate(container, os)) persist(os);
    });
  });
}

function setupSnippetCopy() {
  document.addEventListener('click', async (event) => {
    const target = event.target as HTMLElement | null;
    const button = target?.closest<HTMLElement>('[data-snippet-copy]');
    if (!button) return;

    const snippet = button
      .closest<HTMLElement>('div')
      ?.querySelector<HTMLElement>('[data-snippet-source]');
    const code = snippet?.textContent ?? '';
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }

    const icon = button.querySelector<HTMLElement>('[data-snippet-icon]');
    button.setAttribute('data-copied', 'true');
    if (icon) icon.textContent = 'check';
    setTimeout(() => {
      button.removeAttribute('data-copied');
      if (icon) icon.textContent = 'content_copy';
    }, 1500);
  });
}

function setupTocSpy() {
  const links = document.querySelectorAll<HTMLAnchorElement>('[data-toc-link]');
  if (links.length === 0) return;

  const targetById = new Map<string, HTMLAnchorElement>();
  links.forEach((link) => {
    const id = link.getAttribute('data-target');
    if (id) targetById.set(id, link);
  });

  const headings = Array.from(targetById.keys())
    .map((id) => document.getElementById(id))
    .filter((el): el is HTMLElement => !!el);

  if (headings.length === 0) return;

  const setActive = (id: string | null) => {
    links.forEach((link) => {
      const target = link.getAttribute('data-target');
      if (target && target === id) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  let active: string | null = null;
  const observer = new IntersectionObserver(
    (entries) => {
      // Pick the topmost intersecting heading.
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible.length > 0) {
        const id = visible[0].target.id;
        if (id !== active) {
          active = id;
          setActive(id);
        }
      }
    },
    { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
  );

  headings.forEach((h) => observer.observe(h));
  // Default: highlight first heading.
  setActive(headings[0].id);
}

function setupIndexFilters() {
  const filters = document.querySelectorAll<HTMLElement>('[data-filter]');
  const cards = document.querySelectorAll<HTMLElement>('[data-card]');
  if (filters.length === 0 || cards.length === 0) return;

  const apply = (filter: string) => {
    cards.forEach((card) => {
      const cat = card.getAttribute('data-category');
      const show = filter === 'all' || cat === filter;
      card.toggleAttribute('hidden', !show);
    });
    filters.forEach((f) => {
      const isMatch = f.getAttribute('data-filter') === filter;
      f.setAttribute('aria-selected', isMatch ? 'true' : 'false');
    });
  };

  filters.forEach((filter) => {
    filter.addEventListener('click', () => {
      const f = filter.getAttribute('data-filter') ?? 'all';
      apply(f);
    });
  });

  // Honour ?filter=setup deep links from breadcrumbs.
  const params = new URLSearchParams(location.search);
  const initial = params.get('filter');
  if (initial) apply(initial);
}

function setupIndexSearch() {
  const input = document.querySelector<HTMLInputElement>('[data-search]');
  const cards = document.querySelectorAll<HTMLElement>('[data-card]');
  if (!input || cards.length === 0) return;

  input.addEventListener('input', () => {
    const q = input.value.trim().toLowerCase();
    cards.forEach((card) => {
      if (!q) {
        card.toggleAttribute('hidden', false);
        return;
      }
      const haystack = (card.getAttribute('data-search-text') ?? '').toLowerCase();
      card.toggleAttribute('hidden', !haystack.includes(q));
    });
  });
}

function setupRating() {
  const root = document.querySelector<HTMLElement>('[data-rating]');
  if (!root) return;

  const slug = root.getAttribute('data-rating-slug');
  if (!slug) return;

  const buttons = Array.from(
    root.querySelectorAll<HTMLButtonElement>('[data-rating-value]'),
  );
  const summary = root.querySelector<HTMLElement>('[data-rating-summary]');
  const thanks = root.querySelector<HTMLElement>('[data-rating-thanks]');
  const errorEl = root.querySelector<HTMLElement>('[data-rating-error]');
  const prompt = root.querySelector<HTMLElement>('[data-rating-prompt]');
  if (buttons.length === 0 || !summary) return;

  const anonId = getOrCreateAnonId();
  if (!anonId) {
    summary.textContent = 'Rating unavailable in private browsing.';
    buttons.forEach((b) => b.setAttribute('disabled', ''));
    return;
  }

  const renderSummary = (agg: RatingAggregate) => {
    if (!agg || agg.ratingCount === 0) {
      summary.textContent = 'No ratings yet — be the first.';
      return;
    }
    const avg = agg.rating.toFixed(1);
    const noun = agg.ratingCount === 1 ? 'reader' : 'readers';
    summary.textContent = `${avg} / 5 average · ${agg.ratingCount} ${noun}`;
  };

  const setSelected = (value: number | null) => {
    buttons.forEach((b) => {
      const v = Number(b.getAttribute('data-rating-value'));
      b.setAttribute('aria-checked', value !== null && v === value ? 'true' : 'false');
    });
  };

  const setBusy = (busy: boolean) => {
    buttons.forEach((b) => {
      if (busy) b.setAttribute('disabled', '');
      else b.removeAttribute('disabled');
    });
  };

  // Initial fetch.
  fetch(`/api/resources/${encodeURIComponent(slug)}/rating?anonId=${encodeURIComponent(anonId)}`)
    .then((res) => (res.ok ? res.json() : null))
    .then((agg: RatingAggregate | null) => {
      if (!agg) return;
      renderSummary(agg);
      if (agg.userRating) {
        setSelected(agg.userRating);
        if (prompt) prompt.textContent = "You've rated this guide. Tap a different face to change it.";
      }
    })
    .catch(() => {
      // Silent — keep default summary copy.
    });

  buttons.forEach((button) => {
    button.addEventListener('click', async () => {
      const value = Number(button.getAttribute('data-rating-value'));
      if (!Number.isInteger(value) || value < 1 || value > 5) return;

      setBusy(true);
      setSelected(value);
      if (errorEl) errorEl.hidden = true;

      try {
        const res = await fetch(`/api/resources/${encodeURIComponent(slug)}/rate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ value, anonId }),
        });
        if (!res.ok) {
          const body: { error?: string } = await res.json().catch(() => ({}));
          throw new Error(body.error || `request failed (${res.status})`);
        }
        const agg: RatingAggregate = await res.json();
        renderSummary(agg);
        if (thanks) {
          thanks.hidden = false;
          setTimeout(() => {
            if (thanks) thanks.hidden = true;
          }, 3000);
        }
      } catch (err) {
        if (errorEl) {
          errorEl.textContent = err instanceof Error ? err.message : 'Could not submit rating.';
          errorEl.hidden = false;
        }
        setSelected(null);
      } finally {
        setBusy(false);
      }
    });
  });
}

function setupReadTracking() {
  const slugEl = document.querySelector<HTMLElement>('[data-rating-slug]');
  const slug = slugEl?.getAttribute('data-rating-slug');
  if (!slug) return;

  try {
    if (sessionStorage.getItem(READ_SENT_KEY_PREFIX + slug)) return;
  } catch {
    // sessionStorage blocked — proceed without dedup.
  }

  const anonId = getOrCreateAnonId();
  if (!anonId) return;

  fetch(`/api/resources/${encodeURIComponent(slug)}/read`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ anonId }),
    keepalive: true,
  })
    .then((res) => {
      if (!res.ok) return;
      try {
        sessionStorage.setItem(READ_SENT_KEY_PREFIX + slug, '1');
      } catch {
        // sessionStorage blocked — every load will re-send, acceptable.
      }
    })
    .catch(() => {
      // Fire-and-forget; analytics failures must not affect the user.
    });
}

function init() {
  setupOSTabs();
  setupSnippetCopy();
  setupTocSpy();
  setupIndexFilters();
  setupIndexSearch();
  setupRating();
  setupReadTracking();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
